import type { Dayjs } from 'dayjs';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';

import { HeadFilterTextField } from './head-filter-text-field';
import { HeadFilterSelectField } from './head-filter-select-field';
import { HeadFilterCustomField } from './head-filter-custom-field';
import { HeadFilterBooleanField } from './head-filter-boolean-field';
import { HeadFilterDateTimeField } from './head-filter-datetime-field';
import { HeadFilterMultiSelectField } from './head-filter-multi-select-field';

import type { TableHeadCellProps } from '../table-head-custom';

// ----------------------------------------------------------------------

type FilterPopoverProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  selectedColumn: TableHeadCellProps | null;
  filterValue: any;
  setFilterValue: (value: any) => void;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  setFromDate: (date: Dayjs | null) => void;
  setToDate: (date: Dayjs | null) => void;
  dateFieldColumns: string[];
  onApply: () => void;
  onReset: () => void;
  externalData: any;
  t: (key: string) => string;
  modifiedTypes: any;
};

// ----------------------------------------------------------------------

export function FilterPopover({
  open,
  anchorEl,
  onClose,
  selectedColumn,
  filterValue,
  setFilterValue,
  fromDate,
  toDate,
  setFromDate,
  setToDate,
  dateFieldColumns,
  onApply,
  onReset,
  externalData,
  t,
  modifiedTypes,
}: FilterPopoverProps) {
  const { text, select, multiSelect } = modifiedTypes;
  const dropDownFields = ['status', 'priority', 'direction', 'channelType'];
  const customColumns = ['labels', 'lifecycleStage'];

  const getOptions = () => {
    if (!selectedColumn) return [];

    // Use options from column definition if available
    if (selectedColumn.options && selectedColumn.options.length > 0) {
      return selectedColumn.options.map((option: any) => {
        if (
          typeof option === 'object' &&
          option !== null &&
          'value' in option &&
          'label' in option
        ) {
          return option;
        }
        return {
          value: option,
          label: option,
        };
      });
    }

    return [];
  };

  const renderFilterField = () => {
    if (!selectedColumn) return null;

    const { type, id } = selectedColumn;

    // Switch case for different filter types
    switch (true) {
      // DateTime filter
      case dateFieldColumns.includes(id):
        return (
          <HeadFilterDateTimeField
            fromDate={fromDate}
            toDate={toDate}
            setFromDate={setFromDate}
            setToDate={setToDate}
            t={t}
          />
        );

      // Custom filter (labels, lifecycle)
      case customColumns.includes(id):
        return (
          <HeadFilterCustomField
            filterValue={filterValue}
            setFilterValue={setFilterValue}
            selectedColumn={selectedColumn}
            externalData={externalData}
            t={t}
          />
        );

      // Text filter
      case type === text:
        return (
          <HeadFilterTextField
            filterValue={filterValue}
            setFilterValue={setFilterValue}
            selectedColumn={selectedColumn}
            t={t}
          />
        );

      // Select filter
      case type === select || dropDownFields.includes(id):
        return (
          <HeadFilterSelectField
            filterValue={filterValue}
            setFilterValue={setFilterValue}
            options={getOptions()}
            selectedColumn={selectedColumn}
            t={t}
          />
        );

      // Multi-select filter
      case type === multiSelect:
        return (
          <HeadFilterMultiSelectField
            filterValue={filterValue}
            setFilterValue={setFilterValue}
            options={getOptions()}
            selectedColumn={selectedColumn}
            t={t}
          />
        );

      // Boolean filter
      case type === 'boolean':
        return (
          <HeadFilterBooleanField
            filterValue={filterValue}
            setFilterValue={setFilterValue}
            selectedColumn={selectedColumn}
            t={t}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
    >
      <Box
        sx={{ padding: '15px 10px 5px 10px' }}
        display="flex"
        flexDirection="column"
        gap={1}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.stopPropagation();
            event.preventDefault();
            onApply();
          }
        }}
      >
        {renderFilterField()}

        <Box display="flex" justifyContent="flex-end" gap={1}>
          <Button size="small" onClick={onReset}>
            {t('RESET')}
          </Button>
          <Button size="small" onClick={onClose}>
            {t('CANCEL')}
          </Button>
          <Button
            size="small"
            onClick={onApply}
            disabled={
              selectedColumn && dateFieldColumns.includes(selectedColumn.id)
                ? !!(fromDate && !toDate) || !!(!fromDate && toDate)
                : false
            }
          >
            {t('APPLY')}
          </Button>
        </Box>
      </Box>
    </Popover>
  );
}
